import {
	FreeOrDiscountedInitiationOptionLabels,
	OutcomeMeasureTypeLabels,
	RoundingOptionLabels,
	VolumeMeasureOptionLabels,
} from "../../../../models/simulator/options";
import currencyFormat from "../../../../utils/currency-format";
import { TFunction } from "react-i18next";
import { IScenarioDiscountModel } from "../../services/reporting-service";
import { isEmptyValue, sumBy } from "@lyfegen-platform/utils";
import { IIndicationInput } from "../../services/inputs";
import moment from "moment";
import { IMonthlyPatientSplit } from "../../services/patient-service";
import { GROUP_LABEL } from "../../services/model-service";

export const formatCurrencyValue = (value: number, round: 1000 | 1000000 | undefined, maxFactorial?: number) => {
	const defaultMaxFactorial = maxFactorial ?? 2;
	const minFactorial = defaultMaxFactorial < 2 ? 0 : 2;

	if (!round || isEmptyValue(value)) return currencyFormat(value);

	return currencyFormat(value / round, defaultMaxFactorial, minFactorial) + (round === 1000 ? "k" : "M");
};
export const totalDiscount = (scenarioDiscounts: Array<IScenarioDiscountModel>) => {
	return sumBy(scenarioDiscounts, (scenarioDiscount) => scenarioDiscount.discount);
};

export const getRoundingValue = (rounding: RoundingOptionLabels) => {
	switch (rounding) {
		case RoundingOptionLabels.ROUND_TO_THOUSANDS:
			return 1000;
		case RoundingOptionLabels.ROUND_TO_MILLIONS:
			return 1000000;
		default:
			return undefined;
	}
};
export const sumDiscounts = (discounts: Array<IScenarioDiscountModel>) => {
	return discounts.reduce((acc, v) => acc + v.discount, 0);
};

export const splitConditionTypeThreshold = (outcomeName?: string, str?: string, measureType?: OutcomeMeasureTypeLabels) => {
	if (!str) {
		return [""];
	}

	if (measureType === OutcomeMeasureTypeLabels.TIME_TO_TREATMENT_DISCONTINUATION) {
		const evThreshold = str.match(/(\d+)\s+(\w+)/);

		return `${evThreshold?.[1]} ${evThreshold?.[2]}`;
	}

	return outcomeName;
};

export const splitEvDuration = (str?: string) => {
	if (!str) {
		return [""];
	}

	const evThreshold = str.match(/(\d+\s+\w+)$/);

	return evThreshold?.[1];
};

export const sliceNameByVolumeMeasure = (
	t: TFunction<"translation", undefined>,
	argsInfo: {
		scenarioName: string;
		simCurrency: string;
		packDosage: string;
		type?: string;
	}
) => {
	const { type, scenarioName, simCurrency, packDosage } = argsInfo;
	const isRange = !isNaN(+scenarioName[0]);

	switch (type) {
		case VolumeMeasureOptionLabels.ACTIVE_INGREDIENT:
			return isRange ? applyRangeFormatting(t, scenarioName.split(" - "), packDosage) : scenarioName + packDosage;
		case VolumeMeasureOptionLabels.QUANTITY:
			return isRange ? applyRangeFormatting(t, scenarioName.split(" - "), "") : scenarioName;
		case VolumeMeasureOptionLabels.TREATMENT_COST:
			return isRange ? applyRangeFormatting(t, scenarioName.split(" - "), simCurrency) : scenarioName + " " + simCurrency;
		case FreeOrDiscountedInitiationOptionLabels.DAYS_OF_TREATMENT:
			return isRange ? applyRangeFormatting(t, scenarioName.split(" - "), t("days")) : scenarioName + t("days");
		default:
			return scenarioName;
	}
};

const applyRangeFormatting = (t: TFunction<"translation", undefined>, range: string[], unit: string) => {
	return `${range[0]} ${unit} ${t("to")} ${range[1]} ${unit}`;
};

export const toFixedNumber = (value: number) => {
	return parseFloat(value.toFixed(4));
};

export const getDynamicMaxHeight = () => {
	const screenWidth = window.innerWidth || 1280;
	const baseWidth = 1280;
	const baseHeight = 240;
	const maxHeight = 460;
	const ratio = Math.max(1, screenWidth / baseWidth);
	const scaled = baseHeight * ratio;

	return Math.min(scaled, maxHeight);
};

export function mapCohortsByAdministration(simulationOutput: IMonthlyPatientSplit[]) {
	const cohortsMap = new Map();

	for (const monthData of simulationOutput) {
		for (const patient of monthData.activePatients) {
			const cohortId = patient.id;
			if (!cohortsMap.has(cohortId)) {
				const cohortStartDate = moment(monthData.currentMonth).format("DD-MM-YYYY");
				cohortsMap.set(cohortId, {
					cohortStartDate,
					cohort: "Cohort " + (+patient.treatmentStartMonth + 1),
					administrationEntries: new Map<string, any[]>(),
					...patient,
				});
			}

			const cohort = cohortsMap.get(cohortId);
			for (const pack of patient.distributedPacks) {
				const dateKey = moment(pack.administrationDate).format("YYYY-MM-DD");
				if (!cohort.administrationEntries.has(dateKey)) {
					cohort.administrationEntries.set(dateKey, []);
				}

				cohort.administrationEntries.get(dateKey).push({ ...pack, numberOfPatients: patient.numberOfPatients });
			}
		}
	}

	return Array.from(cohortsMap.values()).map((cohort) => {
		const administrationEntries = Array.from(cohort.administrationEntries.entries()).map(([date, packs]: any) => ({
			administrationDate: moment(date).format("YYYY-MM-DD"),
			packs,
		}));

		return {
			...cohort,
			administrationEntries,
		};
	});
}

export const findIndicationName = (indications: IIndicationInput[], indicationId: string) =>
	indications?.find((ind) => ind.id === indicationId.split(GROUP_LABEL)[0])?.name;
